import { useCallback, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { AdyenPaymentCompletedResult } from 'src/components/AdyenPayment/AdyenDropIn';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  useCreateAdyenPaymentSession,
  useSubmitAdyenPaymentResult,
  useSubmitPaymentWithStoredMethod,
} from 'src/hooks/api/useAdyenPaymentSession';
import { useApplyExternalPayment } from 'src/hooks/api/useApplyExternalPayment';
import { useKnownCountries } from 'src/hooks/api/useKnownCountries';
import { useUpsertBuyer } from 'src/hooks/api/useUpsertBuyer';
import { getSaleDetailsRelativeUrl } from 'src/utils/saleUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ApiException,
  BuyerUpsertDto,
  BuyerWithSalesDto,
  EventWithData,
  GenericAddressDto,
  ListingDetails,
  SaleClient,
  SaleLineItemCoverage,
} from 'src/WebApiController';

import {
  buildRetailSaleGroupInput,
  getPaymentAmount,
  getPaymentCountryCode,
  getRetailMerchantReferenceId,
} from './RetailSaleLocalContext.utils';
import { RetailSaleFormValues } from './types';

export type AdyenSession = {
  saleId: number;
  salesGroupId: string | null;
  sessionId: string;
  sessionData: string;
  clientKey: string;
  environment: string;
  merchantReference: string;
  countryCode: string | null;
  lineItemCoverages: SaleLineItemCoverage[] | null;
};

type UseRetailSaleOperationsParams = {
  event: EventWithData | undefined;
  listing: ListingDetails | undefined;
  selectedBuyer: BuyerWithSalesDto | undefined;
  selectedStoredPaymentMethodId: string | null;
  setSelectedBuyer: (buyer: BuyerWithSalesDto) => void;
  isPartialPayment: boolean;
  partialAmount: number;
};

export const useRetailSaleOperations = ({
  listing,
  selectedBuyer,
  selectedStoredPaymentMethodId,
  setSelectedBuyer,
  isPartialPayment,
  partialAmount,
}: UseRetailSaleOperationsParams) => {
  const { getValues } = useFormContext<RetailSaleFormValues>();
  const { showErrorDialog, genericError, trackError } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const navigate = useNavigate();

  const createPaymentSession = useCreateAdyenPaymentSession();
  const submitPaymentResult = useSubmitAdyenPaymentResult();
  const submitPaymentWithStoredMethod = useSubmitPaymentWithStoredMethod();
  const applyExternalPayment = useApplyExternalPayment();
  const upsertBuyer = useUpsertBuyer();
  const knownCountries = useKnownCountries();

  // Reference to track the created sale
  // In case of payment error, we want to avoid creating multiple sales for the same listing
  const saleIdRef = useRef<number | null>(null);
  // Every retail sale belongs to a sales group (a single sale is a group of one); Adyen
  // sessions/payments/refunds key off this group id.
  const salesGroupIdRef = useRef<string | null>(null);

  const [adyenSession, setAdyenSession] = useState<AdyenSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [coverageDialogSaleId, setCoverageDialogSaleId] = useState<
    number | null
  >(null);
  const [coverageDialogSalesGroupId, setCoverageDialogSalesGroupId] = useState<
    string | null
  >(null);

  const disableEditing = useMemo(() => {
    return isLoading || Boolean(adyenSession);
  }, [adyenSession, isLoading]);

  const onCompleteAsUnpaid = useCallback(() => {
    const values = getValues();
    setIsLoading(true);
    const groupInput = buildRetailSaleGroupInput(
      values,
      listing,
      selectedBuyer
    );

    tryInvokeApi(
      async () => {
        const client = new SaleClient(activeAccountWebClientConfig);
        const result = await client.createRetailSaleGroup(groupInput);
        const saleId = result.sales?.[0]?.saleId;

        if (result.success && saleId) {
          navigate(getSaleDetailsRelativeUrl(saleId));
        } else {
          showErrorDialog(
            'SaleClient.createRetailSaleGroup',
            {
              message: result.message ?? genericError,
              status: 500,
            } as ApiException,
            { trackErrorData: groupInput }
          );
        }
      },
      (error) => {
        showErrorDialog('SaleClient.createRetailSaleGroup', error, {
          trackErrorData: groupInput,
        });
      },
      () => {
        setIsLoading(false);
      }
    );
  }, [
    activeAccountWebClientConfig,
    genericError,
    getValues,
    listing,
    navigate,
    selectedBuyer,
    showErrorDialog,
  ]);

  /**
   * Submits the retail sale when the broker selects "Paid Externally".
   *
   * Full payment (isPartialPayment === false):
   *   Sends external payment fields directly with createRetailSaleGroup
   *   so the sale is created as Paid in a single call (Step 1 path).
   *
   * Partial payment (isPartialPayment === true):
   *   1. Creates the sale as Unpaid (no external fields on create)
   *   2. Opens the PartialPaymentCoverageDialog with the new saleId
   *   3. onCoverageConfirmed (external branch) calls ApplyExternalPayment with
   *      the selected coverages and external payment details.
   */
  const onCompleteExternalPayment = useCallback(() => {
    const values = getValues();
    setIsLoading(true);

    if (isPartialPayment) {
      // Step 2 partial path: create sale as Unpaid (if not already created),
      // then open coverage dialog.
      if (saleIdRef.current) {
        // Sale already created (e.g. user cancelled the dialog and retried) —
        // just re-open the dialog without creating another sale.
        setCoverageDialogSaleId(saleIdRef.current);
        setCoverageDialogSalesGroupId(salesGroupIdRef.current);
        setIsLoading(false);
        return;
      }

      // Omit external fields so the sale is created Unpaid; ApplyExternalPayment
      // will be called in onCoverageConfirmed after coverages are selected.
      const groupInput = buildRetailSaleGroupInput(
        values,
        listing,
        selectedBuyer,
        false
      );

      tryInvokeApi(
        async () => {
          const client = new SaleClient(activeAccountWebClientConfig);
          const result = await client.createRetailSaleGroup(groupInput);
          const saleId = result.sales?.[0]?.saleId;

          if (!result.success || !saleId) {
            showErrorDialog(
              'SaleClient.createRetailSaleGroup',
              {
                message: result.message ?? genericError,
                status: 500,
              } as ApiException,
              { trackErrorData: groupInput }
            );
            return;
          }

          saleIdRef.current = saleId;
          salesGroupIdRef.current = result.salesGroupId;
          setCoverageDialogSaleId(saleId);
          setCoverageDialogSalesGroupId(result.salesGroupId);
          // isLoading stays true until coverages are confirmed or dialog is cancelled
        },
        (error) => {
          showErrorDialog('SaleClient.createRetailSaleGroup', error, {
            trackErrorData: groupInput,
          });
        },
        () => {
          // Only stop loading on the error path (saleIdRef not set).
          // If we opened the coverage dialog, loading remains on until
          // onCoverageConfirmed or onCoverageDialogCancel resolves.
          if (!saleIdRef.current) {
            setIsLoading(false);
          }
        }
      );
    } else {
      // Step 1 full-payment path: include external fields so the sale is created Paid.
      const groupInput = buildRetailSaleGroupInput(
        values,
        listing,
        selectedBuyer,
        true
      );

      tryInvokeApi(
        async () => {
          const client = new SaleClient(activeAccountWebClientConfig);
          const result = await client.createRetailSaleGroup(groupInput);
          const saleId = result.sales?.[0]?.saleId;

          if (result.success && saleId) {
            navigate(getSaleDetailsRelativeUrl(saleId));
          } else {
            showErrorDialog(
              'SaleClient.createRetailSaleGroup',
              {
                message: result.message ?? genericError,
                status: 500,
              } as ApiException,
              { trackErrorData: groupInput }
            );
          }
        },
        (error) => {
          showErrorDialog('SaleClient.createRetailSaleGroup', error, {
            trackErrorData: groupInput,
          });
        },
        () => {
          setIsLoading(false);
        }
      );
    }
  }, [
    activeAccountWebClientConfig,
    genericError,
    getValues,
    isPartialPayment,
    listing,
    navigate,
    selectedBuyer,
    setCoverageDialogSaleId,
    setCoverageDialogSalesGroupId,
    showErrorDialog,
  ]);

  const onPayAndComplete = useCallback(() => {
    const storedMethodId = selectedStoredPaymentMethodId;
    const values = getValues();
    setIsLoading(true);

    tryInvokeApi(
      async () => {
        if (!saleIdRef.current) {
          // Create the sale as Unpaid (omit external fields); the Adyen session/
          // payment below settles it.
          const groupInput = buildRetailSaleGroupInput(
            values,
            listing,
            selectedBuyer,
            false
          );
          const client = new SaleClient(activeAccountWebClientConfig);
          const placeholderResult =
            await client.createRetailSaleGroup(groupInput);
          const placeholderSaleId = placeholderResult.sales?.[0]?.saleId;

          if (!placeholderResult.success || !placeholderSaleId) {
            showErrorDialog(
              'SaleClient.createRetailSaleGroup',
              {
                message: placeholderResult.message ?? genericError,
                status: 500,
              } as ApiException,
              { trackErrorData: groupInput }
            );
            return;
          }

          saleIdRef.current = placeholderSaleId;
          salesGroupIdRef.current = placeholderResult.salesGroupId;
        }

        // If partial payment, open the coverage dialog and let user assign amounts
        if (isPartialPayment) {
          setCoverageDialogSaleId(saleIdRef.current);
          setCoverageDialogSalesGroupId(salesGroupIdRef.current);
          return; // tryInvokeApi's finally block sets isLoading = false
        }

        const amount = getPaymentAmount(values);
        const merchantReference = getRetailMerchantReferenceId(
          saleIdRef.current
        );

        try {
          if (storedMethodId) {
            await submitPaymentWithStoredMethod.mutateAsync({
              salesGroupId: salesGroupIdRef.current,
              storedPaymentMethodId: storedMethodId,
              amount,
              currencyCode: values.currencyCode ?? 'USD',
              buyerEmail: values.buyerEmail ?? null,
              reference: merchantReference,
              lineItemCoverages: null,
            });
            navigate(getSaleDetailsRelativeUrl(saleIdRef.current));
          } else {
            const countryCode = getPaymentCountryCode(
              selectedBuyer,
              knownCountries.data
            );
            const sessionResponse = await createPaymentSession.mutateAsync({
              salesGroupId: salesGroupIdRef.current,
              amount,
              currencyCode: values.currencyCode ?? 'USD',
              buyerEmail: values.buyerEmail ?? null,
              returnUrl: window.location.href,
              reference: merchantReference,
              countryCode,
              lineItemCoverages: null,
            });

            setAdyenSession({
              saleId: saleIdRef.current,
              salesGroupId: salesGroupIdRef.current,
              sessionId: sessionResponse.sessionId,
              sessionData: sessionResponse.sessionData,
              clientKey: sessionResponse.clientKey,
              environment: sessionResponse.environment,
              merchantReference,
              countryCode,
              lineItemCoverages: null,
            });
          }
        } catch (error: any) {
          trackError('AdyenPayment.onError', new Error(error.message));
          navigate(getSaleDetailsRelativeUrl(saleIdRef.current));
        }
      },
      (error) => {
        showErrorDialog('RetailSale.onPayAndComplete', error, {
          trackErrorData: { listingId: listing?.id ?? values.listingId },
        });
      },
      () => {
        setIsLoading(false);
      }
    );
  }, [
    activeAccountWebClientConfig,
    createPaymentSession,
    genericError,
    getValues,
    isPartialPayment,
    knownCountries.data,
    listing,
    navigate,
    selectedBuyer,
    selectedStoredPaymentMethodId,
    showErrorDialog,
    submitPaymentWithStoredMethod,
    trackError,
  ]);

  const onAdyenPaymentCompleted = useCallback(
    async (result: AdyenPaymentCompletedResult) => {
      if (!adyenSession) return;

      if (result.resultCode !== 'Authorised') {
        setIsLoading(false);
        setAdyenSession(null);
        showErrorDialog(
          'RetailSale.onPayAndComplete',
          {
            message: `Payment not authorized. Result code: ${result.resultCode}`,
          },
          {
            trackErrorData: {
              resultCode: result.resultCode,
              saleId: adyenSession.saleId,
            },
          }
        );
        return;
      }

      await tryInvokeApi(
        async () => {
          await submitPaymentResult.mutateAsync({
            salesGroupId: adyenSession.salesGroupId,
            input: {
              resultCode: result.resultCode,
              sessionResult: result.sessionResult ?? null,
              merchantReference: adyenSession.merchantReference,
              pspReference: null,
              lineItemCoverages: adyenSession.lineItemCoverages,
            },
          });

          navigate(getSaleDetailsRelativeUrl(adyenSession.saleId));
        },
        (error) => {
          showErrorDialog('RetailSale.onPayAndComplete', error, {
            trackErrorData: {
              listingId: listing?.id,
              saleId: adyenSession.saleId,
            },
          });
        },
        () => {
          setIsLoading(false);
        }
      );
    },
    [
      adyenSession,
      listing?.id,
      navigate,
      setIsLoading,
      showErrorDialog,
      submitPaymentResult,
    ]
  );

  const onAdyenError = useCallback(
    (error: { message: string }) => {
      trackError('AdyenDropIn.onError', new Error(error.message));
      setAdyenSession(null);
    },
    [setAdyenSession, trackError]
  );

  const onCoverageConfirmed = useCallback(
    (coverages: SaleLineItemCoverage[]) => {
      setCoverageDialogSaleId(null);
      setCoverageDialogSalesGroupId(null);
      const storedMethodId = selectedStoredPaymentMethodId;
      const values = getValues();
      setIsLoading(true);
      tryInvokeApi(
        async () => {
          // External partial flow — call ApplyExternalPayment with coverages.
          // This is an early return; the Adyen branch below is left completely unchanged.
          if (values.paymentMethodChoice === 'external') {
            await applyExternalPayment.mutateAsync({
              saleId: saleIdRef.current!,
              input: {
                externalPaymentMethodType: values.externalPaymentMethodType!,
                externalPaymentNote: values.externalPaymentNote ?? null,
                externalPaymentAmount: partialAmount,
                externalPaymentDate: values.externalPaymentDate ?? null,
                coverages,
              },
            });
            navigate(getSaleDetailsRelativeUrl(saleIdRef.current!));
            return;
          }

          // === ADYEN PARTIAL FLOW — UNCHANGED BELOW ===
          const merchantReference = getRetailMerchantReferenceId(
            saleIdRef.current!
          );
          try {
            if (storedMethodId) {
              await submitPaymentWithStoredMethod.mutateAsync({
                salesGroupId: salesGroupIdRef.current,
                storedPaymentMethodId: storedMethodId,
                amount: partialAmount,
                currencyCode: values.currencyCode ?? 'USD',
                buyerEmail: values.buyerEmail ?? null,
                reference: merchantReference,
                lineItemCoverages: coverages,
              });
              navigate(getSaleDetailsRelativeUrl(saleIdRef.current!));
            } else {
              const countryCode = getPaymentCountryCode(
                selectedBuyer,
                knownCountries.data
              );
              const sessionResponse = await createPaymentSession.mutateAsync({
                salesGroupId: salesGroupIdRef.current,
                amount: partialAmount,
                currencyCode: values.currencyCode ?? 'USD',
                buyerEmail: values.buyerEmail ?? null,
                returnUrl: window.location.href,
                reference: merchantReference,
                countryCode,
                lineItemCoverages: coverages,
              });
              setAdyenSession({
                saleId: saleIdRef.current!,
                salesGroupId: salesGroupIdRef.current,
                sessionId: sessionResponse.sessionId,
                sessionData: sessionResponse.sessionData,
                clientKey: sessionResponse.clientKey,
                environment: sessionResponse.environment,
                merchantReference,
                countryCode,
                lineItemCoverages: coverages,
              });
            }
          } catch (error: any) {
            trackError('AdyenPayment.onError', new Error(error.message));
            navigate(getSaleDetailsRelativeUrl(saleIdRef.current!));
          }
        },
        (error) => {
          showErrorDialog('RetailSale.onCoverageConfirmed', error, {
            trackErrorData: { saleId: saleIdRef.current },
          });
        },
        () => {
          setIsLoading(false);
        }
      );
    },
    [
      applyExternalPayment,
      createPaymentSession,
      getValues,
      knownCountries.data,
      navigate,
      partialAmount,
      selectedBuyer,
      selectedStoredPaymentMethodId,
      setAdyenSession,
      showErrorDialog,
      submitPaymentWithStoredMethod,
      trackError,
    ]
  );

  const onCoverageDialogCancel = useCallback(() => {
    setCoverageDialogSaleId(null);
    setCoverageDialogSalesGroupId(null);
    setIsLoading(false);
  }, []);

  const onUpsertBuyer = useCallback(async (): Promise<boolean> => {
    const values = getValues();

    const needsUpsert =
      !selectedBuyer ||
      !selectedBuyer.shippingAddressLine1 ||
      !selectedBuyer.shippingCity ||
      !selectedBuyer.shippingState ||
      !selectedBuyer.shippingZipCode ||
      !selectedBuyer.shippingCountry ||
      !selectedBuyer.billingAddressLine1 ||
      !selectedBuyer.billingCity ||
      !selectedBuyer.billingState ||
      !selectedBuyer.billingZipCode ||
      !selectedBuyer.billingCountry;

    if (!needsUpsert) return true;

    const buildAddress = (
      street: string | undefined,
      suite: string | undefined,
      city: string | undefined,
      state: string | undefined,
      zipCode: string | undefined,
      country: string | undefined
    ): GenericAddressDto | null => {
      if (!street && !state && !zipCode && !country) return null;
      return {
        genericAddressId: 0,
        addressLine1: street ?? null,
        addressLine2: suite ?? null,
        city: city ?? null,
        state: state ?? null,
        zipCode: zipCode ?? null,
        country: country ?? null,
      };
    };

    const shippingAddress = buildAddress(
      values.shippingStreet,
      values.shippingSuite,
      values.shippingCity,
      values.shippingState,
      values.shippingZipCode,
      values.shippingCountry
    );
    const billingAddress = values.billingAddressSameAsShipping
      ? shippingAddress
      : buildAddress(
          values.billingStreet,
          values.billingSuite,
          values.billingCity,
          values.billingState,
          values.billingZipCode,
          values.billingCountry
        );

    const dto: BuyerUpsertDto = {
      buyerEmail: selectedBuyer?.buyerEmail ?? values.buyerEmail!,
      buyerName: selectedBuyer?.buyerName ?? values.buyerName ?? null,
      buyerPhoneNumber:
        selectedBuyer?.buyerPhoneNumber ?? values.buyerPhone ?? null,
      internalNotes:
        selectedBuyer?.internalNotes ?? values.customerMessage ?? null,
      lastRetailActivityDate: null,
      shippingAddress,
      billingAddress,
    };

    setIsLoading(true);
    try {
      await upsertBuyer.mutateAsync(dto);

      setSelectedBuyer({
        ...(selectedBuyer ?? {}),
        buyerEmail: dto.buyerEmail,
        buyerName: dto.buyerName,
        buyerPhoneNumber: dto.buyerPhoneNumber,
        internalNotes: selectedBuyer?.internalNotes ?? null,
        lastRetailActivityDate: selectedBuyer?.lastRetailActivityDate ?? null,
        shippingAddressLine1: shippingAddress?.addressLine1 ?? null,
        shippingAddressLine2: shippingAddress?.addressLine2 ?? null,
        shippingCity: shippingAddress?.city ?? null,
        shippingState: shippingAddress?.state ?? null,
        shippingZipCode: shippingAddress?.zipCode ?? null,
        shippingCountry: shippingAddress?.country ?? null,
        billingAddressLine1: billingAddress?.addressLine1 ?? null,
        billingAddressLine2: billingAddress?.addressLine2 ?? null,
        billingCity: billingAddress?.city ?? null,
        billingState: billingAddress?.state ?? null,
        billingZipCode: billingAddress?.zipCode ?? null,
        billingCountry: billingAddress?.country ?? null,
        cardBrand: selectedBuyer?.cardBrand ?? null,
        cardSummary: selectedBuyer?.cardSummary ?? null,
        expiryMonth: selectedBuyer?.expiryMonth ?? null,
        expiryYear: selectedBuyer?.expiryYear ?? null,
        saleIds: selectedBuyer?.saleIds ?? null,
      });

      return true;
    } catch {
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [getValues, selectedBuyer, setIsLoading, setSelectedBuyer, upsertBuyer]);

  return {
    disabled: disableEditing,
    adyenSession,
    onUpsertBuyer,
    onCompleteAsUnpaid,
    onCompleteExternalPayment,
    onPayAndComplete,
    onAdyenPaymentCompleted,
    onAdyenError,
    coverageDialogSaleId,
    coverageDialogSalesGroupId,
    onCoverageConfirmed,
    onCoverageDialogCancel,
  };
};
